import download from 'downloadjs';

import { SHORT_MONTHS } from './constants';

export const noOp = () => {};

export const getClassNames = (baseClassName, modifiers) => {
  const classes = [baseClassName];

  Object.entries(modifiers).forEach(([name, active]) => {
    if (!active) return;

    classes.push(`${baseClassName}--${name}`);
  });

  return classes.join(' ');
};

export const removeCamelCase = (string) => {
  let copy = string.repeat(1);
  copy = copy
    .replace(/([A-Z]+)/g, ' $1')
    .replace(/([A-Z][a-z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase());
  return copy;
};

export const isEmpty = (value) => !value || value.length === 0;

export const mapStringOptionsToObjectOptions = (stringOptions) => {
  return stringOptions.map((string) => ({
    label: string,
    value: string
  }));
};

export const openInNewTab = (url) => {
  if (!url) return;

  let copyOfUrl = url.repeat(1);

  if (!url.startsWith('http')) {
    copyOfUrl = `http://${url}`;
  }

  window.open(copyOfUrl, '_blank', 'noopener, noreferrer');
};

export const isNumeric = (string) => {
  if (typeof string != 'string') return false;
  return !isNaN(string) && !isNaN(parseFloat(string));
};

export const generateUUID = () => crypto.randomUUID();

export const saveToLocalStorage = (key, value) => localStorage.setItem(key, JSON.stringify(value));

export const retrieveFromLocalStorage = (key) => JSON.parse(localStorage.getItem(key));

export const downloadFile = (data, name) => {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(new Blob([data]));
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadExcelFile = (base64String, name) => download(window.atob(base64String), name);

export const fileToBase64 = async (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => resolve(reader.result.split('base64,')[1]);
    reader.onerror = (error) => reject(error);
  });

export const extractWordAtCaretPosition = (string, caretPosition) => {
  let wordStartPosition = caretPosition;
  let wordEndPosition;

  if (string.charAt(caretPosition) === ' ' || string.charAt(caretPosition) === '') {
    wordEndPosition = caretPosition;
  } else {
    for (let i = caretPosition; i <= string.length; i++) {
      wordEndPosition = i;
      if (string.charAt(i) === ' ') {
        break;
      }
    }
  }

  let hasRunOnce = false;

  for (let i = caretPosition; i >= 0; i--) {
    if (hasRunOnce) {
      if (string.charAt(i) === ' ') {
        break;
      }
    }

    wordStartPosition = i;
    hasRunOnce = true;
  }

  const word = string.slice(wordStartPosition, wordEndPosition);

  return [word, wordStartPosition, wordEndPosition];
};

export const replaceWordAtCaretPosition = (string, wordToInsert, caretPosition) => {
  const [wordToReplace, wordStartPosition] = extractWordAtCaretPosition(string, caretPosition);

  const stringArray = [...string];

  stringArray.splice(wordStartPosition, wordToReplace.length, ...wordToInsert);

  return stringArray.join('');
};

export const isHashtag = (string) => {
  if (string.includes(' ')) return false;
  if (string[0] === '#') return true;
  return false;
};

export const isMention = (string) => {
  if (string.includes(' ')) return false;
  if (string[0] === '@') return true;
  return false;
};

export const convertTimeStringToDate = (timeString) => {
  if (!timeString) {
    throw new Error('Please provide a time string');
  }

  const [hours, minutes] = timeString.split(':');

  const date = new Date();
  date.setUTCHours(hours);
  date.setUTCMinutes(minutes);
  date.setUTCSeconds(0);
  date.setUTCMilliseconds(0);

  return date;
};

export const convertDateToTimeString = (date) => {
  const dateToFormat = new Date(date);

  let hours = dateToFormat.getUTCHours();
  let minutes = dateToFormat.getUTCMinutes();

  if (hours < 10) {
    hours = `0${hours}`;
  }

  if (minutes < 10) {
    minutes = `0${minutes}`;
  }

  return `${hours}:${minutes}`;
};

export const isProgrammeIncomplete = (programme) => {
  if (!programme) return true;

  const { planExternalReference, accountabilityExists, supportExternalReference, tribeExternalReference } = programme;

  if (!planExternalReference || !accountabilityExists || !supportExternalReference || !tribeExternalReference) {
    return true;
  }

  return false;
};

export const base64ToImage = (base64String, imageType) => {
  if (!base64String) return undefined;

  return `data:image/${imageType};base64,${base64String}`;
};

export const fileToImage = (file) => {
  return URL.createObjectURL(file);
};

export const isImage = (fileName) => {
  const fileExtension = fileName.split('.').pop()?.toLocaleLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension);
};

export const isVideo = (fileName) => {
  const fileExtension = fileName.split('.').pop()?.toLocaleLowerCase();
  return ['mp4'].includes(fileExtension);
};

export const containsImageOrVideo = (files) => {
  if (!files) return false;

  return files.some((file) => isImage(file.name) || isVideo(file.name));
};

export const getProfilePictureForMember = (members, memberUsername) => {
  const member = members.find((member) => member.username === memberUsername);

  if (!member) return undefined;

  return member.profilePicture;
};

export const isToday = (date) => {
  const today = new Date();
  const dateToCheck = new Date(date);

  return (
    dateToCheck.getDate() === today.getDate() &&
    dateToCheck.getMonth() === today.getMonth() &&
    dateToCheck.getFullYear() === today.getFullYear()
  );
};

export const formatProgrammeDates = (from, to) => {
  const fromDate = new Date(from);
  const fromDay = fromDate.getDate();
  const fromMonth = SHORT_MONTHS[fromDate.getMonth()];
  const fromYear = fromDate.getFullYear();

  const toDate = new Date(to);
  const toDay = toDate.getDate();
  const toMonth = SHORT_MONTHS[toDate.getMonth()];
  const toYear = toDate.getFullYear();

  let showFirstYear = false;
  if (fromYear !== toYear) showFirstYear = true;

  let showFirstMonth = false;
  if (fromMonth !== toMonth) showFirstMonth = true;

  return `${fromDay} ${showFirstMonth ? fromMonth : ''} ${showFirstYear ? fromYear : ''} -
    ${toDay} ${toMonth} ${toYear}`;
};

export const formatUsername = (username) => {
  if (!username) return undefined;

  if (username.includes('Deleted Account')) {
    return 'Deleted Account';
  }

  return username;
};

export const validateSouthAfricanIdNumber = (idNumber) => {
  if (!idNumber || idNumber.length !== 13) return false;

  const idYear = idNumber.substring(0, 2);
  const idMonth = idNumber.substring(2, 4);
  const idDay = idNumber.substring(4, 6);

  try {
    const date = new Date(idYear, idMonth - 1, idDay);

    const dateYear = date.getFullYear().toString();

    let dateMonth = (date.getMonth() + 1).toString();
    if (dateMonth < 10) {
      dateMonth = `0${dateMonth}`;
    } else {
      dateMonth = dateMonth.toString();
    }

    let dateDay = date.getDate();
    if (dateDay < 10) {
      dateDay = `0${dateDay}`;
    } else {
      dateDay = dateDay.toString();
    }

    if (dateYear.slice(2, 4) !== idYear) return false;
    if (dateMonth !== idMonth) return false;
    if (dateDay !== idDay) return false;
  } catch (error) {
    return false;
  }

  const gender = parseInt(idNumber.substring(6, 10));
  if (gender < 1 || gender > 9999) return false;

  const citizenship = idNumber.substring(10, 11);
  if (citizenship !== '0' && citizenship !== '1') return false;

  let checkSum = 0;
  for (let i = 0; i < idNumber.length; i++) {
    let value = parseInt(idNumber[i]);
    if (i % 2 !== 0) {
      value *= 2;

      if (value > 9) {
        value -= 9;
      }
    }

    checkSum += value;
  }

  if (checkSum % 10 !== 0) return false;

  return true;
};

/**
 * Returns an array of options for the weeks in the programme.
 * The number of weeks is calculated from the difference between
 * the from and to dates of the programme.
 *
 * The options are in the format of { label: 1, value: 1 }.
 *
 * @param {object} programme
 * @returns {array} options
 */
export const getWeekOptions = (programme) => {
  if (!programme) return [];

  const fromDate = new Date(programme.fromDate);
  const toDate = new Date(programme.toDate);

  const daysInProgramme = (toDate - fromDate) / (1000 * 60 * 60 * 24);
  const weeksInProgramme = Math.ceil(daysInProgramme / 7);

  const options = [];

  for (let i = 1; i <= weeksInProgramme; i++) {
    options.push({ label: i.toString(), value: i.toString() });
  }

  return options;
};
